<template>
  <Template.Define>
    <div
      class="absolute"
      :class="[
        iconLocation === 'left' && `left-4`,
        iconLocation === 'right' && `right-4`,
        variant === 'secondary' && `text-muted-foreground`,
      ]"
    >
      <ArrowPathIcon v-if="pending" class="size-4 animate-spin" />
      <component :is="resolvedIcon" v-else-if="resolvedIcon" class="size-4" />
      <slot v-else name="icon" />
    </div>

    <div class="flex-1 text-center">
      {{ text }}
      <slot></slot>
    </div>

    <div
      v-if="hotkey"
      :class="[
        'hidden rounded-lg px-2 font-mono text-sm uppercase lg:block',
        variant === 'primary' && 'bg-black/50',
        variant === 'secondary' && 'bg-black/15',
      ]"
    >
      {{ formatKeybind(hotkey) }}
    </div>
  </Template.Define>

  <RouterLink
    v-if="to"
    ref="elRef"
    v-bind="$attrs"
    :aria-label="text"
    :aria-description="description"
    :class="cls"
    :to="to"
    @click="onClick"
  >
    <Template.Use />
  </RouterLink>
  <a
    v-else-if="href"
    ref="elRef"
    v-bind="$attrs"
    :aria-label="text"
    :aria-description="description"
    :class="cls"
    :href="href"
    :target="target"
    @click="onClick"
  >
    <Template.Use />
  </a>
  <button
    v-else
    ref="elRef"
    v-bind="$attrs"
    :type="type ?? (!formId ? 'button' : 'submit')"
    :form="formId"
    :aria-label="text"
    :aria-description="description"
    :class="cls"
    :disabled="disabled"
    @click="onClick"
  >
    <Template.Use />
  </button>
</template>

<script lang="ts" setup>
import { type Component, computed, normalizeClass, ref } from "vue";
import { useRouter } from "vue-router";

import {
  type MappedBrandIcon,
  brandIconMapping,
} from "@/icons/brand-icon-mapping";
import ArrowPathIcon from "@/icons/heroicons/arrow-path-micro.svg";
import {
  type MappedIconMicro,
  iconMappingMicro,
} from "@/icons/icon-mapping-micro";
import { createReusableTemplate } from "@/lib/composables/template";
import { formatKeybind, useKeybinds } from "@/lib/hotkeys";
import type { RouteLocation } from "@/router/types";

const Template = createReusableTemplate();

const router = useRouter();

export type Variant = "primary" | "secondary" | "negative" | "subtle";

export type Props = {
  to?: RouteLocation;
  href?: string;
  icon?: MappedIconMicro | MappedBrandIcon | Component;
  text: string;
  description?: string;
  pending?: boolean;
  variant?: Variant;
  iconLocation?: "left" | "right";
  size?: "sm" | "md";
  hotkey?: string;
  disabled?: boolean;
  target?: string;
  type?: "submit" | "button";
  formId?: string;
  onClick?: (ev: Event) => void;
};

const elRef = ref();
const props = withDefaults(defineProps<Props>(), {
  variant: "secondary",
  iconLocation: "left",
  size: "md",
});

defineOptions({
  inheritAttrs: false,
});

const resolvedIcon = computed(() => {
  if (!props.icon) return null;
  if (typeof props.icon === "string" && props.icon in iconMappingMicro) {
    return iconMappingMicro[props.icon as MappedIconMicro];
  }
  if (typeof props.icon === "string" && props.icon in brandIconMapping) {
    return brandIconMapping[props.icon as MappedBrandIcon];
  }
  return props.icon;
});

const cls = computed(() => {
  const variant = props.variant;
  const disabled = props.disabled;
  const size = props.size;

  return normalizeClass([
    `relative inline-flex w-full items-center whitespace-nowrap border text-sm font-medium transition`,
    `outline-2 focus-visible:outline`,
    disabled && `cursor-not-allowed opacity-50`,
    size === "md" && `rounded-xl px-4 py-2`,
    size === "sm" && `rounded-2xl px-3 py-1`,
    variant === "secondary" &&
      `border-gray-200 bg-white text-gray-700 shadow-sm -outline-offset-1 outline-blue-500 hover:bg-gray-50 active:bg-gray-50 dark:border-border dark:bg-card dark:text-foreground dark:hover:bg-sidebar dark:active:bg-sidebar`,
    variant === "primary" &&
      `border-transparent bg-blue-500 text-white shadow-sm outline-offset-2 outline-blue-500 hover:bg-blue-600 active:bg-blue-600`,
    variant === "negative" &&
      `border-transparent bg-red-500 text-white shadow-sm outline-red-500 hover:bg-red-600 active:bg-red-600`,
    variant === "subtle" &&
      `border-transparent text-muted-foreground outline-blue-500 hover:bg-card hover:text-foreground active:bg-card active:text-foreground`,
  ]);
});

useKeybinds(() => {
  const hotkey = props.hotkey;
  if (!hotkey || props.disabled) {
    return;
  }

  return {
    [hotkey]: {
      name: props.description ?? props.text,
      run(ev) {
        if (ev.defaultPrevented) {
          return;
        }

        if (props.to) {
          router.push(props.to);
        } else if (props.href) {
          window.location.href = props.href;
        } else {
          elRef.value?.click();
        }

        return true;
      },
    },
  };
});
</script>
